import * as React from "react";
import { Card, CardContent, Paper, Typography } from "@mui/material";
import parse from "html-react-parser";
import { DatePicker, Input } from "rsuite";
import PhoneNumberInput from "components/common/PhoneNumberInput";
import "./contact-form.css";
import { useIntl } from "react-intl";
import moment from "moment";

export default function ContactForm({
  contactFormConfig = null,
  contactFormData = {},
  updateContactFormData,
  contactFormErrors = {},
}) {
  const intl = useIntl();
  const falbackPlaceholder = intl.formatMessage({
    id: "enter-value-here",
  });

  if (!contactFormConfig) {
    return null;
  }

  return (
    <Paper elevation={3} className="mt-2">
      <Card sx={{ maxWidth: 345 }}>
        <Typography
          variant="h6"
          gutterBottom
          style={{
            textAlign: "center",
          }}
          className="mt-2"
        >
          {contactFormConfig.contact_info_title ||
            intl.formatMessage({ id: "contact-information" })}
        </Typography>

        <CardContent>
          {contactFormConfig.contact_info_description ? (
            <div style={{ marginBottom: "15px" }} className="contact-info">
              {parse(
                contactFormConfig.contact_info_description || ""
              )}
            </div>
          ) : null}

          {/* Standard Contact Fields */}
          {contactFormConfig.contact_fields?.name?.enabled && (
            <div className="mt-3">
              <h6 className="fw-normal">
                {contactFormConfig.contact_fields.name.label || "Name"}
                {contactFormConfig.contact_fields.name.required ? (
                  <span style={{ color: "red" }}>*</span>
                ) : null}
              </h6>
              <div className="mt-1">
                <Input
                  id="contact-name"
                  variant="outlined"
                  placeholder={
                    contactFormConfig.contact_fields.name.placeholder ||
                    falbackPlaceholder
                  }
                  value={contactFormData?.name || ""}
                  onChange={(e) => updateContactFormData(e, "name")}
                />
              </div>
              {contactFormErrors?.name?.error ? (
                <p className="text-danger mt-1">
                  {contactFormErrors.name.message}
                </p>
              ) : null}
            </div>
          )}

          {contactFormConfig.contact_fields?.email?.enabled && (
            <div className="mt-3">
              <h6 className="fw-normal">
                {contactFormConfig.contact_fields.email.label || "Email"}
                {contactFormConfig.contact_fields.email.required ? (
                  <span style={{ color: "red" }}>*</span>
                ) : null}
              </h6>
              <div className="mt-1">
                <Input
                  id="contact-email"
                  type="email"
                  variant="outlined"
                  placeholder={
                    contactFormConfig.contact_fields.email.placeholder ||
                    falbackPlaceholder
                  }
                  value={contactFormData?.email || ""}
                  onChange={(e) => updateContactFormData(e, "email")}
                />
              </div>
              {contactFormErrors?.email?.error ? (
                <p className="text-danger mt-1">
                  {contactFormErrors.email.message}
                </p>
              ) : null}
            </div>
          )}

          {contactFormConfig.contact_fields?.phone?.enabled && (
            <div className="mt-3">
              <h6 className="fw-normal">
                {contactFormConfig.contact_fields.phone.label || "Phone"}
                {contactFormConfig.contact_fields.phone.required ? (
                  <span style={{ color: "red" }}>*</span>
                ) : null}
              </h6>
              <div className="mt-1">
                <PhoneNumberInput
                  id="contact-phone"
                  name="phone"
                  placeholder={
                    contactFormConfig.contact_fields.phone.placeholder ||
                    falbackPlaceholder
                  }
                  value={contactFormData?.phone || ""}
                  defaultCountry={contactFormData?.phoneCountry}
                  onChange={(phone, country) => {
                    updateContactFormData(phone || "", "phone");
                    if (country) {
                      updateContactFormData(country, "phoneCountry");
                    }
                  }}
                />
              </div>
              {contactFormErrors?.phone?.error ? (
                <p className="text-danger mt-1">
                  {contactFormErrors.phone.message}
                </p>
              ) : null}
            </div>
          )}

          {/* Custom Contact Fields */}
          {contactFormConfig.contact_custom_fields?.map((field) => (
            <div className="mt-3" key={field.fieldKey}>
              <h6 className="fw-normal">
                {field.fieldName}
                {field.isRequired ? (
                  <span style={{ color: "red" }}>*</span>
                ) : null}
              </h6>
              <div className="mt-1">
                {field.fieldType === "date" ? (
                  <DatePicker
                    format="DD/MM/YYYY"
                    ranges={[]}
                    value={contactFormData?.[field.fieldKey]}
                    onChange={(value) =>
                      updateContactFormData(
                        moment(value).format("YYYY-MM-DD"),
                        field.fieldKey
                      )
                    }
                    className="form-control p-0"
                    block
                    style={{ color: "darkgray", fontSize: "14px" }}
                    placeholder={field.placeholder || falbackPlaceholder}
                  />
                ) : field.fieldType === "time" ? (
                  <DatePicker
                    format="HH:mm"
                    ranges={[]}
                    value={contactFormData?.[field.fieldKey]}
                    onChange={(value) =>
                      updateContactFormData(
                        moment(value).format("HH:mm"),
                        field.fieldKey
                      )
                    }
                    className="form-control p-0"
                    block
                    style={{ color: "darkgray", fontSize: "14px" }}
                    placeholder={field.placeholder || falbackPlaceholder}
                  />
                ) : field.fieldType === "datetime" ? (
                  <DatePicker
                    format="DD/MM/YYYY HH:mm"
                    ranges={[]}
                    value={contactFormData?.[field.fieldKey]}
                    onChange={(value) =>
                      updateContactFormData(
                        moment(value).format("YYYY-MM-DD HH:mm"),
                        field.fieldKey
                      )
                    }
                    className="form-control p-0"
                    block
                    style={{ color: "darkgray", fontSize: "14px" }}
                    placeholder={field.placeholder || falbackPlaceholder}
                  />
                ) : field.fieldType === "number" ? (
                  <Input
                    type="number"
                    variant="outlined"
                    placeholder={field.placeholder || falbackPlaceholder}
                    value={contactFormData?.[field.fieldKey] || ""}
                    onChange={(e) => updateContactFormData(e, field.fieldKey)}
                  />
                ) : field.fieldType === "phone" ? (
                  <PhoneNumberInput
                    name={field.fieldKey}
                    value={contactFormData?.[field.fieldKey] || ""}
                    defaultCountry={
                      contactFormData?.[`${field.fieldKey}Country`]
                    }
                    onChange={(phone, country) => {
                      updateContactFormData(phone || "", field.fieldKey);
                      if (country) {
                        updateContactFormData(
                          country,
                          `${field.fieldKey}Country`
                        );
                      }
                    }}
                    placeholder={field.placeholder || falbackPlaceholder}
                  />
                ) : (
                  <Input
                    variant="outlined"
                    placeholder={field.placeholder || falbackPlaceholder}
                    value={contactFormData?.[field.fieldKey] || ""}
                    onChange={(e) => updateContactFormData(e, field.fieldKey)}
                  />
                )}
              </div>
              {contactFormErrors?.[field.fieldKey]?.error ? (
                <p className="text-danger mt-1">
                  {contactFormErrors[field.fieldKey].message}
                </p>
              ) : null}
            </div>
          ))}
        </CardContent>
      </Card>
    </Paper>
  );
}
